<template>
  <div id="app" data-app>
    <app-header />
    <h2 class="big-title">{{ txc.heading }}</h2>
    <div class="form-container">
      <NsVTextField
        ref="sapId"
        v-model="model.sapId"
        class="flex-column"
        data-testid="sap_id"
        required
        :required-label="commonStrings.forms.fieldRequired"
        :label="txc.dist_id_label"
        :type="text"
        :rules="sapIdRules"
        validate-on-blur
        @input="checkSapId()"
      >
      </NsVTextField>
      <NsEmail
        v-model="model.email"
        :label="txc.email_label"
        :validation-service-url="
          'https://www.nuskin.com/validate-service/api/v1/email/address.json?address=' +
          encodeEmailValue
        "
        validation-service-valid-response-path="data.valid"
        required
        :required-error-message="commonStrings.forms.fieldRequired"
        :invalid-email-text="commonStrings.forms.fieldBadEmail"
        data-testid="email"
        maxlength
        @error-state="isEmailValid"
      />
      <NsOktaPassword
        v-model="model.password"
        data-testid="password"
        :username="model.email"
        :translations="passwordTranslations"
        validate-on-blur
        @valid="passwordValid = $event"
      />
      <div v-if="enableEmailOptIn">
        <div class="policies">{{ txc.email_optin_summary }}</div>
        <NsCheckBox
          v-model="model.emailOptIn"
          enabled
          data-testid="email-opt-in-checkbox"
          icon-name="icon-checkmark"
          :label="txc.email_optin_label"
        />
      </div>

      <div class="refundLink">
        <a
          id="showRefundDialogBtn"
          :href="txc.refund_link.href"
          target="_blank"
        >
          {{ txc.refund_link.title }}
        </a>

        <!-- Commented until link discussions around this with execs happens
        <a id="showRefundDialogBtn" href="#" @click.stop="refundDialog = true">
          {{ txc.refund_link.title }}
        </a>

        <pnp-modal
          v-if="refundDialog"
          :modal-title="txc.refund_link.title"
          :pnp-html="refundHtml"
          @closeDialog="refundDialog = false"
        />
        -->
      </div>

      <div class="pnpLink">
        <div class="policies">{{ txc.policies_description }}</div>

        <a id="showPnpDialogBtn" :href="txc.pnp_link.href" target="_blank">
          {{ txc.pnp_link.title }}
        </a>

        <!-- Commented until link discussions around this with execs happens
        <a id="showPnpDialogBtn" href="#" @click.stop="pnpDialog = true">
          {{ txc.pnp_link.title }}
        </a>

        <pnp-modal
          v-if="pnpDialog"
          :modal-title="txc.pnp_link.title"
          :pnp-html="pnpHtml"
          @closeDialog="pnpDialog = false"
        />
        -->
      </div>

      <NsCheckBox
        v-model="policyCheck"
        enabled
        data-testid="pnp-checkbox"
        icon-name="icon-checkmark"
        :label="txc.pnp_label"
      />

      <button
        class="primary-solid submit-btn"
        data-testid="submit"
        :text="txc.continue"
        :disabled="
          !policyCheck ||
          !emailValid ||
          !sapIdValid ||
          !passwordValid ||
          serverIsBusy
        "
        @click="onSubmit()"
      >
        {{ commonStrings.generic.continue }}
      </button>
    </div>
    <NsToast
      data-testid="toast"
      :success="toastSuccess"
      :error="!toastSuccess"
      icon
      :message="toastMessage"
      :show-trigger="showTrigger"
    />
    <app-footer />
    <div v-if="serverIsBusy" id="spinner" class="overlay-container">
      <NsSpinner class="spinner" />
    </div>
  </div>
</template>

<script>
import { SignupService } from '@nuskin/ns-account'
import Stack from './service'
import WebAccountCreateSrv from './webAccountCreateSrv'
import AppHeader from './AppHeader.vue'
import AppFooter from './AppFooter.vue'
// import PnpModal from './PnpModal.vue'
import {
  NsEmail,
  NsOktaPassword,
  NsCheckBox,
  NsSpinner,
  NsToast,
  NsVTextField,
} from '@nuskin/design-components'
import axios from 'axios'
import webAccountCreateSrv from './webAccountCreateSrv'
export default {
  name: 'App',
  components: {
    NsVTextField,
    NsEmail,
    NsOktaPassword,
    NsCheckBox,
    NsToast,
    NsSpinner,
    AppHeader,
    AppFooter,
    // PnpModal,
  },
  data: function () {
    return {
      pnpDialog: false,
      refundDialog: false,
      showTrigger: true,
      toastSuccess: true,
      toastMessage: '',
      policyCheck: false,
      emailValid: false,
      sapIdValid: false,
      passwordValid: false,
      enableEmailOptIn: false,
      serverIsBusy: false,
      sapIdRules: [
        (v) => (v || '').length > 0 || this.commonStrings.forms.fieldRequired,
        () => this.sapIdValid || this.commonStrings.forms.invalid,
      ],
      txc: {
        pnp_link: {
          title: '',
          href: '',
        },
        refund_link: {
          title: '',
          href: '',
        },
      },
      commonStrings: {
        generic: {},
        error: {},
        password: {},
        accounts: {},
        forms: {
          fieldRequired: 'required',
        },
      },
      pnpHtml: '',
      refundHtml: '',
      passwordTranslations: {},
      model: {
        sapId: '',
        loginId: '',
        email: '',
        password: '',
        emailOptIn: false,
      },
      sapRegex: /^[A-Z]{2}[0-9]{7}|[A-Z]{3}[0-9]{7}|[A-Z]{2}[0-9]{8}|[A-Z]{3}[0-9]{8}$/i,
      redirectUrl: '',
    }
  },
  computed: {
    encodeEmailValue() {
      return encodeURIComponent(this.model.email)
    },
  },
  async created() {
    this.getMarketConfig()
    await this.getStrings()
    this.getCommonStrings()
    const [pnpHtml, refundHtml] = await Promise.allSettled([
      this.getHtml(axios.get, this.txc.pnp_link.href),
      this.getHtml(axios.get, this.txc.refund_link.href),
    ])
    this.pnpHtml = pnpHtml.value
    this.refundHtml = refundHtml.value
  },
  methods: {
    getMarketConfig() {
      try {
        webAccountCreateSrv.getMarketConfig(this)
      } catch (e) /* istanbul ignore next */ {
        console.log('Error in getMarketConfig', e)
      }
    },
    async getStrings() {
      let strings
      try {
        strings = await Stack.getEntry('web_account_create')
      } catch (e) /* istanbul ignore next */ {
        console.log('Error in getEntry: ', e)
      }

      this.txc = {
        ...this.txc,
        ...strings[0],
      }
    },
    async getHtml(get, url) {
      let data = ''
      try {
        if (url !== '') {
          data = (await get(url)).data
        }
      } catch (e) {
        console.log('Error getting asset: ', e)
      }
      return data
    },
    async getCommonStrings() {
      let commonStrings = {}
      try {
        for (let i = 0; i < this.txc.shared_strings.length; i++) {
          const element = this.txc.shared_strings[i]
          let common = await Stack.getSubEntry(
            element._content_type_uid,
            element.uid
          )
          commonStrings[common.key] = common.strings.reduce(
            (result, { key, value }) => ({ ...result, [key]: value }),
            {}
          )
        }
      } catch (e) /* istanbul ignore next */ {
        console.log('Error in getCommonStrings: ', e)
      }

      this.commonStrings = commonStrings

      this.passwordTranslations = {
        ...commonStrings.password,
        hide: commonStrings.forms?.hide,
        show: commonStrings.forms?.show,
        newPassword: commonStrings.password?.passwordLabel,
      }
    },
    async emailCheck() {
      try {
        return await SignupService.isUsernameAvailable(this.model.email)
      } catch (err) {
        console.error(`Failed to check if account exists ${err}`)
        return false
      }
    },
    checkSapId() {
      this.model.sapId = this.model.sapId.toUpperCase()
      if (this.model.sapId.startsWith('CN')) {
        this.sapIdValid = false
      } else {
        this.sapIdValid = this.sapRegex.test(this.model.sapId)
      }
    },
    isEmailValid(event) {
      /* istanbul ignore else */
      if (event.isValid) this.emailValid = true
    },
    createWebAccount() {
      // make the email also the login
      this.model.loginId = this.model.email
      WebAccountCreateSrv.finishSignup(this)
    },
    showSuccessToast() {
      this.serverIsBusy = false
      this.toastSuccess = true
      this.toastMessage = this.txc.toast_messages.web_create_success
      this.showTrigger = !this.showTrigger
      let self = this
      if (self.redirectUrl && self.redirectUrl !== '') {
        setTimeout(() => {
          window.location = self.redirectUrl
        }, 100)
      }
    },
    showErrorToast() {
      this.serverIsBusy = false
      this.toastSuccess = false
      this.toastMessage = this.commonStrings.error.error_occurred
      this.showTrigger = !this.showTrigger
    },
    async onSubmit() {
      this.serverIsBusy = true
      let emailCheck = await this.emailCheck()
      if (emailCheck.status) {
        this.showErrorToast()
      } else {
        return this.createWebAccount()
      }
    },
  },
}
</script>

<style lang="scss">
.overlay-container {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.5);
}

#app {
  .form-container {
    min-width: 400px;
    max-width: 700px;
    padding: 40px;
    margin: 0 auto;
    margin-top: 40px;
    background: white;
    border: 1px solid #ddd;
  }
  .policies {
    padding-bottom: 15px;
  }
  .pnpLink {
    margin-bottom: 25px;
  }
  button.submit-btn {
    margin-top: 25px;
  }
  .big-title {
    text-align: center;
  }
  .ns-email {
    padding-top: 16px;
  }
  .ns-checkbox {
    padding-bottom: 16px;
  }
  input[data-testid='sap_id'],
  input[name='email'] {
    padding-left: 15px;
  }
}
</style>
